import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Box, styled, MenuItem, ClickAwayListener, Popper, Paper, CircularProgress } from '@mui/material'
import { Tooltip, theme } from 'packages/eid-ui'
import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'

const styles = {
    small: {
        minHeight: '35px',
        lineHeight: '1 !important',
    },
    medium: {
        height: '30px',
        padding: '4px 5px',
        // lineHeight: '1 !important',
    },
    large: {
        minHeight: '36px',
        padding: '4px 8px',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '1 !important',
    },
}

// Mapping from new status names to existing status configurations
const statusMapping = {
    'Not Validated': 'NotStarted',
    'Valid': 'Approved',
    'Invalid': 'Expired',
    'Ignored': 'Deferred',
    'Completed': 'Completed',
    'Error': 'Error',
    'Publish Queued': 'WaitingOnGlobalFulfillment',
    'Publishing': 'Continued',
    'Closed': 'AutoClosed',
}

const statuses = {
    Approve: {
        colors: {},
        gradient: ' linear-gradient(101deg, #01ae8f, #30c17c)',
    },
    Active: {
        colors: {},
        gradient: ' linear-gradient(101deg, #01ae8f, #30c17c)',
    },
    Approved: {
        colors: {
            backgroundColor: '#e1fee6',
            color: '#1eb77b',
        },
        icon: <Icon name="ApprovedStatus" color="#1EB758" />,
        gradient: 'linear-gradient(to right, #1eb758 0%, #1eb7a6 100%)',
    },
    AutoClosed: {
        icon: (
            <Icon
                name="AutoClosed"
                color="#ffffff"
                fill="#ffffff"
                height="18px"
                direction="right"
            />
        ),
        colors: { backgroundColor: '#5d6870', color: '#ffffff' },
    },

    Back: {
        icon: (
            <Icon
                name="ArrowRoundedBorders"
                fill="#ffffff"
                height="18px"
                direction="left"
            />
        ),
        colors: { backgroundColor: '#0057A0', color: '#ffffff' },
    },

    Canceled: {
        colors: {
            backgroundColor: '#5d6870',
            color: '#ffffff',
        },
        icon: <Icon name="CanceledStatus" color="#ffffff" />,
    },
    Completed: {
        colors: {
            backgroundColor: '#e7f4ff',
            color: '#007fc7',
        },

        icon: <Icon name="CompletedStatus" />,
        gradient: 'linear-gradient(to right, #30acc1 0%, #3077c1 100%)',
    },
    Continued: {
        icon: (
            <Icon
                name="ArrowRoundedBorders"
                color="#0057A0"
                fill="#0057A0"
                height="18px"
                direction="right"
            />
        ),
        colors: { backgroundColor: '#e7f4ff', color: '#0057A0' },
    },

    Deferred: {
        icon: (
            <Icon
                name="ArrowCurvedRoundedBorders"
                color="#ffb800"
                fill="#ffffff"
                height="18px"
                direction="right"
            />
        ),
        colors: { backgroundColor: '#5d6870', color: '#ffffff' },
    },
    Delegated: {
        icon: (
            <Icon
                name="ArrowCurvedRoundedBorders"
                color="#ffb800"
                fill="#0057A0"
                height="20px"
                direction="right"
            />
        ),
        colors: { backgroundColor: '#e7f4ff', color: '#0057A0' },
    },

    Error: {
        colors: {
            backgroundColor: '#d0021b',
            color: '#ffffff',
        },
        icon: <Icon name="ErrorStatus" color="#ffffff" />,
        gradient: 'linear-gradient(to right, #8a0111 0%, #af2000 100%)',
    },
    Expired: {
        colors: {
            backgroundColor: '#d0021b33',
            color: '#d0021b',
        },
        icon: <Icon name="ErrorStatus" color="#d0021b" />,
        gradient: 'linear-gradient(to right, #d00245 0%, #d64d00 100%);',
    },

    NotStarted: {
        colors: {
            backgroundColor: '#aab0b4',
            color: '#5b6871',
        },
        icon: <Icon name="NotStartedStatus" />,
        gradient: 'linear-gradient(to right, #5d6870, #90a0ab)',
    },
    NotReady: {
        colors: {
            backgroundColor: '#aab0b4',
            color: '#5b6871',
        },
        icon: <Icon name="NotStartedStatus" />,
        gradient: 'linear-gradient(to right, #5d6870, #90a0ab)',
    },
    Next: {
        icon: (
            <Icon
                name="ArrowRoundedBorders"
                fill="#ffffff"
                height="18px"
                direction="right"
            />
        ),
        colors: { backgroundColor: '#0057A0', color: '#ffffff' },
    },

    Open: {
        colors: {
            backgroundColor: '#f9eada',
            color: '#e56300',
        },
        icon: <Icon name="OpenStatus" />,
        gradient: 'linear-gradient(to right, #eda300 0%, #d66a00 100%)',
    },

    Open_PendingApproval: {
        colors: {
            backgroundColor: '#f9eada',
            color: '#e56300',
        },
        icon: <Icon name="OpenStatus" />,
        gradient: 'linear-gradient(to right, #eda300 0%, #d66a00 100%)',
    },
    Open_PendingItemApproval: {
        colors: {
            backgroundColor: '#f9eada',
            color: '#e56300',
        },
        icon: <Icon name="OpenStatus" />,
        gradient: 'linear-gradient(to right, #eda300 0%, #d66a00 100%)',
    },

    PartiallyApproved: {
        colors: {
            backgroundColor: '#e1fee6',
            color: '#1eb77b',
        },
        icon: <Icon name="ApprovedStatus" color="#1EB758" />,
        gradient: 'linear-gradient(to right, #1eb758 0%, #1eb7a6 100%)',
    },

    Reject: {
        gradient: 'linear-gradient(102deg, #ae0142, #c14830 100%)',
    },
    Rejected: {
        colors: {
            backgroundColor: '#f9dada',
            color: '#d0021b',
        },
        icon: <Icon name="RejectedStatus" />,
        gradient: 'linear-gradient(to right, #d00245 0%, #d64d00 100%)',
    },
    ReturnedToSender: {
        icon: <Icon name="ErrorStatus" />,
        colors: { backgroundColor: '#f9dada', color: '#d0021b' },
    },
    Requested: {
        icon: (
            <Icon
                name="ArrowRoundedBorders"
                color="#ffb800"
                fill="#0057A0"
                height="20px"
                direction="right"
            />
        ),
        colors: { backgroundColor: '#e7f4ff', color: '#0057A0' },
    },

    Submit: {
        icon: (
            <Icon
                name="CheckWithWhiteCircle"
                color="red"
                fill="#ffffff"
                height="20px"
            />
        ),
        colors: { backgroundColor: '#0057A0', color: '#ffffff' },
    },

    VotedYes: {
        icon: <Icon name="ThumbsUp" height="18px" />,
        colors: { backgroundColor: '#e1fee6', color: '#1eb77b' },
    },
    VotedNo: {
        icon: <Icon name="ThumbsDown" height="18px" />,
        colors: { backgroundColor: '#f9dada', color: '#d0021b' },
    },

    WaitingOnGlobalFulfillment: {
        colors: {
            backgroundColor: '#fff7e5',
            color: '#ffb800',
        },
        icon: <Icon name="PendingStatus" color="#ffb800" />,
    },

    Waiting: {
        icon: <Icon name="PauseRoundedBorders" />,
        colors: { backgroundColor: '#d8d8dd', color: '#5d6870' },
    },

    Default: {
        colors: {
            backgroundColor: '#d8d8dd',
            color: '#5d6870',
        },
        gradient: 'none',
    },
}

const MiniTag = styled('span')(({ image, width, style, minWidth }) => ({
    display: 'table',
    paddingInline: '0.8rem',
    borderRadius: '0.5rem',
    backgroundColor: '#5d6870',
    textTransform: 'uppercase',
    maxWidth: '100%',
    fontSize: '1.2rem',
    color: theme?.palette?.common?.white,
    backgroundImage: image,
    minWidth: width,

    '& label': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
        lineHeight: '1.6rem',
        maxWidth: style?.minWidth ? style.minWidth : '12.4rem',
        display: 'table-cell',
        verticalAlign: 'middle',
        textAlign: 'center',
    },
    ...style,
}))

const StyledDiv = styled('div')(({ colors, size, minWidth }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    backgroundColor: colors && colors.backgroundColor,
    color: colors ? colors.color : 'black',
    border: ` solid 1px ${colors ? colors.color : 'black'}`,
    borderRadius: '6px',
    fontSize: '13px',
    overflow: 'hidden !important',
    overflowX: 'hidden',
    ...styles[size],
    minWidth: minWidth ? minWidth : '154px',
}))

const StyledMenuItem = styled(MenuItem)({
    height: '32px',
    minHeight: '32px',
    fontSize: '13px',
    justifyContent: 'center',
    '&:hover': {
        backgroundColor: '#f5f6f8',
    },
})

const DropdownArrow = styled(Box)(({ colors }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
    marginLeft: '8px',
    marginTop: '-4px',
    marginBottom: '-4px',
    paddingLeft: '8px',
    paddingRight: '4px',
    borderLeft: `1px solid ${colors?.color || '#5d6870'}60`,
    cursor: 'pointer',
    outline: 'none',
    '&:hover': {
        opacity: 0.8,
    },
    '&:focus-visible': {
        outline: '2px solid #0057A0',
        outlineOffset: '-2px',
        borderRadius: '4px',
    },
}))

const StyledPaper = styled(Paper)({
    marginTop: '4px',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid #e8e8e8',
    overflow: 'hidden',
    minWidth: '100px',
})
const tooltipDelay = 400
const InboxStatusIndicator = (props) => {
    const {
        size,
        variant,
        label,
        width,
        style,
        minWidth,
        dropdownOptionsMap,
        isRowEditable,
        onChange,
        tooltipText,
        isLoading,
    } = props
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [currentStatus, setCurrentStatus] = useState(variant)
    const containerRef = useRef(null)

    // Use currentStatus for display (allows immediate visual update on selection)
    const displayVariant = currentStatus || variant

    // Map new status names to existing status configurations
    const mappedVariant = statusMapping[displayVariant] || displayVariant
    const variantToUse = mappedVariant === 'PreApproved' ? 'Approved' : mappedVariant
    const status = statuses[variantToUse] || statuses['Default']

    // Check if this status has dropdown options (based on current displayed status)
    const dropdownOptions = dropdownOptionsMap?.[displayVariant] || []
    const hasDropdownOptions = dropdownOptions.length > 0 && onChange
    const showDropdownArrow = (hasDropdownOptions || isLoading) && isRowEditable
    // const showDropdownArrow = hasDropdownOptions || isLoading


    const handleArrowClick = (e) => {
        if (isLoading) return
        e.stopPropagation()
        setOpen((prev) => !prev)
    }

    const handleArrowKeyDown = (e) => {
        if (isLoading) return
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            setOpen((prev) => !prev)
        } else if (e.key === 'Escape' && open) {
            e.preventDefault()
            setOpen(false)
        }
    }

    const handleOptionClick = (option) => {
        setOpen(false)

        // Notify parent for API call with onSuccess callback
        if (onChange) {
            const onSuccess = (newStatus) => {
                // Update status only on success
                setCurrentStatus(newStatus)
            }
            onChange(option, onSuccess)
        }
    }

    const closeMenu = () => {
        setOpen(false)
    }

    // Get the display label (use currentStatus for translated label)
    const displayLabel = currentStatus !== variant ? t(currentStatus) : label

    // Render the status content (icon + label only, wrapped in tooltip)
    const renderStatusContent = () => (
        <Tooltip
            title={tooltipText || displayLabel || ''}
            placement="bottom"
            enterDelay={tooltipDelay}
            enterNextDelay={tooltipDelay}
        >
            <Box display="flex" alignItems="center" sx={{ flex: 1 }}>
                <Box display="flex">{status?.icon}</Box>
                <div
                    style={{
                        marginLeft: status?.icon ? '10px' : '0px',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: '1',
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {displayLabel}
                </div>
            </Box>
        </Tooltip>
    )

    // Render the dropdown arrow or loader
    const renderDropdownArrow = () => {
        // Show loader when updating
        if (isLoading) {
            return (
                <DropdownArrow colors={status?.colors} style={{ cursor: 'default' }}>
                    <CircularProgress
                        size={12}
                        sx={{ color: status?.colors?.color || '#5d6870' }}
                    />
                </DropdownArrow>
            )
        }

        return (
            <DropdownArrow
                colors={status?.colors}
                onClick={handleArrowClick}
                onKeyDown={handleArrowKeyDown}
                tabIndex={0}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={t('ChangeStatus')}
            >
                <Icon
                    name="ArrowSmall"
                    color={status?.colors?.color || '#5d6870'}
                    width="10px"
                    fill={status?.colors?.color || '#5d6870'}
                    direction={open ? 'up' : 'down'}
                />
            </DropdownArrow>
        )
    }

    if (size === 'small') {
        return (
            <Tooltip
                title={label ? label : ''}
                placement="bottom"
                enterDelay={tooltipDelay}
                enterNextDelay={tooltipDelay}
            >
                <MiniTag image={status?.gradient} width={width} style={style}>
                    <label>{label}</label>
                </MiniTag>
            </Tooltip>
        )
    }

    return (
        <>
            <StyledDiv
                ref={containerRef}
                colors={status?.colors}
                size={size}
                minWidth={minWidth}
                style={{ position: 'relative' }}
            >
                {renderStatusContent()}
                {showDropdownArrow && renderDropdownArrow()}
            </StyledDiv>

            {hasDropdownOptions && !isLoading && open && (
                <ClickAwayListener onClickAway={closeMenu}>
                    <Popper
                        open={open}
                        anchorEl={containerRef.current}
                        placement="bottom"
                        style={{
                            zIndex: 1300,
                            width: containerRef.current?.offsetWidth || 'auto',
                        }}
                    >
                        <StyledPaper style={{ width: '100%' }}>
                            {dropdownOptions.map((option, index) => (
                                <StyledMenuItem
                                    key={`${option}-${index}`}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleOptionClick(option)
                                    }}
                                >
                                    {t(option)}
                                </StyledMenuItem>
                            ))}
                        </StyledPaper>
                    </Popper>
                </ClickAwayListener>
            )}
        </>
    )
}

InboxStatusIndicator.propTypes = {
    variant: PropTypes.oneOf([
        // New mapped status names
        'Not Validated',
        'Valid',
        'Invalid',
        'Ignored',
        'Completed',
        'Error',
        'Publish Queued',
        'Publishing',
        // Legacy status names (kept for backward compatibility)
        'NotStarted',
        'NotReady',
        'AutoClosed',
        'Approved',
        'PreApproved',
        'Open',
        'Active',
        'Open_PendingItemApproval',
        'Open_PendingApproval',
        'Rejected',
        'Reject',
        'Approve',
        'Default',
        'Expired',
        'Canceled',
        'PartiallyApproved',
        'WaitingOnGlobalFulfillment',
        'Continued',
        'Deferred',
        'Delegated',
        'VotedYes',
        'VotedNo',
        'ReturnedToSender',
        'Requested',
        'Waiting',
        'Submit',
        'Back',
        'Next',
    ]),
    size: PropTypes.string,
    label: PropTypes.string,
    style: PropTypes.object,
    /** Map of status variants to their dropdown options. E.g., { 'Invalid': ['Ignored'], 'Ignored': ['Invalid'] } */
    dropdownOptionsMap: PropTypes.object,
    /** Callback when dropdown option is selected. Receives the new status value and onSuccess callback */
    onChange: PropTypes.func,
    /** Custom tooltip text to display on hover */
    tooltipText: PropTypes.string,
    /** Whether the status is currently being updated (shows loader) */
    isLoading: PropTypes.bool,
}

InboxStatusIndicator.defaultProps = {
    variant: 'Default',
    dropdownOptionsMap: null,
    onChange: null,
    tooltipText: null,
    isLoading: false,
}

export default InboxStatusIndicator
